import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import * as Icons from "lucide-react";
import { armSound, playChime } from "../lib/notificationSound";
import { toast } from "sonner";
import { isMobileDevice } from "../lib/deviceProfile";

const TYPE_ICONS = {
  quote_viewed: "Eye", quote_signed: "PenLine", deposit_paid: "CreditCard", lead_won: "Trophy",
  lead_created: "UserPlus", lead_assigned: "UserCheck", document_uploaded: "FileUp",
  callback_booked: "CalendarClock", sms_received: "MessageSquare", call: "Phone",
};

const timeAgo = (iso) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export const NotificationsBell = ({ align = "left" }) => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const known = useRef(null);
  const mobile = isMobileDevice();
  const [sound, setSound] = useState(localStorage.getItem("gsp_mobile_notification_sound") !== "off");

  const load = useCallback(() => {
    api.get("/notifications").then((r) => {
      const next = r.data.items || [];
      const fresh = known.current && next.filter((n) => !n.read && !known.current.has(n.id));
      const rect = ref.current?.getBoundingClientRect();
      if (fresh?.length && rect?.width && rect.right > 0 && rect.left < innerWidth && !document.hidden) {
        playChime(fresh[0].id);
        const n = fresh.find((n) => n.thread_id);
        if (n) toast(n.message, { id: n.id, action: { label: "Open chat", onClick: () => navigate(`/app/chat?thread=${n.thread_id}`) } });
      }
      known.current = new Set(next.map((n) => n.id)); setItems(next); setUnread(r.data.unread || 0);
    }).catch(() => {});
  }, [navigate]);

  useEffect(() => {
    window.addEventListener("pointerdown", armSound, { once: true });
    return () => window.removeEventListener("pointerdown", armSound);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) {
      api.post("/notifications/read", { ids: [n.id] }).then(load).catch(() => {});
    }
    if (n.thread_id) navigate(`/app/chat?thread=${n.thread_id}`);
    else if (n.lead_id) navigate(`/app/leads/${n.lead_id}`);
  };

  const markAll = () => api.post("/notifications/read", { all: true }).then(load).catch(() => {});

  return (
    <div className="relative h-9 w-9 shrink-0" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`notifications-bell-${align}`} aria-label="Open notifications"
        className="relative h-full w-full p-2 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
        <Icons.Bell size={19} />
        {unread > 0 && (
          <span data-testid={`notifications-unread-${align}`} className="absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] px-1 rounded-full bg-destructive text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className={`absolute ${align === "right" ? "right-0" : "left-0"} mt-2 w-[340px] max-w-[calc(100vw_-_24px)] bg-card border border-border rounded-md shadow-xl z-50 overflow-hidden`} data-testid={`notifications-panel-${align}`}>
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
            <span className="font-semibold text-sm">Notifications</span>
            {unread > 0 && (
              <button onClick={markAll} data-testid={`notifications-mark-all-${align}`} className="text-xs text-secondary font-semibold hover:underline">Mark all read</button>
            )}
          </div>
          <button onClick={() => { setOpen(false); navigate("/app/notifications"); }} data-testid={`notification-settings-link-${align}`} className="w-full px-4 py-2 text-left text-xs text-primary border-b border-border hover:bg-muted transition-colors">Device notifications & Home Screen installation</button>
          {mobile ? <button data-testid={`notification-sound-${align}`} onClick={() => { const enabled = !sound; setSound(enabled); localStorage.setItem("gsp_mobile_notification_sound", enabled ? "on" : "off"); if (enabled) { armSound(); setTimeout(playChime, 100); } }} className="w-full px-4 py-2 text-left text-xs border-b border-border hover:bg-muted flex gap-2 items-center">{sound ? <Icons.Volume2 size={14} /> : <Icons.VolumeX size={14} />}Mobile in-app chime: {sound ? "On" : "Off"}</button> : <div data-testid={`notification-sound-${align}`} className="px-4 py-2 text-xs border-b border-border flex gap-2 items-center"><Icons.VolumeX size={14} />Desktop sound off · notifications remain on</div>}
          <div className="max-h-[380px] overflow-y-auto">
            {items.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No notifications yet</div>}
            {items.map((n) => {
              const Icon = Icons[TYPE_ICONS[n.type] || "Bell"];
              return (
                <button key={n.id} onClick={() => openItem(n)} data-testid={`notification-item-${align}-${n.id}`} data-thread-id={n.thread_id || undefined}
                  className={`w-full text-left px-4 py-3 flex gap-3 border-b border-border/60 last:border-0 hover:bg-muted/60 transition-colors ${!n.read ? "bg-secondary/5" : ""}`}>
                  <span className={`mt-0.5 shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${!n.read ? "bg-secondary/15 text-secondary" : "bg-muted text-muted-foreground"}`}>
                    <Icon size={15} />
                  </span>
                  <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
                    <span className={`block text-sm leading-snug ${!n.read ? "font-semibold text-foreground" : "text-muted-foreground"}`} data-testid={`notification-message-${align}-${n.id}`}>{n.message}</span>
                    <span className="block text-[11px] text-muted-foreground mt-0.5" data-testid={`notification-time-${align}-${n.id}`}>{n.lead_name ? `${n.lead_name} · ` : ""}{timeAgo(n.created_at)}</span>
                  </span>
                  {!n.read && <span className="ml-auto mt-1.5 h-2 w-2 rounded-full bg-secondary shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
